import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { StatusCard } from '@workspace/ui/components/status-card'
import { Handshake } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import {
  ConnectDialog,
  DataRoomContent,
  InvestorCtaModal,
  useDataRoomTracker,
} from '../../../features/pitch'
import {
  useConnectWithStartupMutation,
  useCurrentUserQuery,
  useDataRoomContentQuery,
  useGetDataRoomDocumentDownloadMutation,
  useGetDataRoomVideoUrlMutation,
  useGetPitchDeckDownloadMutation,
  useInvestorQuery,
  useRecordDataRoomViewMutation,
  useRequestInvestorEvaluationMutation,
  useStartupConfigQuery,
} from '../../../hooks/api'
import { useIsFounder, useIsInvestor } from '../../../hooks/use-user-type'
import { setAuthRedirect } from '../../../lib/auth'

const dataRoomSearchSchema = z.object({
  action: z.enum(['request_custom_diligence']).optional(),
})

export const Route = createFileRoute('/_protected/s/$token')({
  component: DataRoomPage,
  validateSearch: dataRoomSearchSchema,
})

function DataRoomPage() {
  const { token } = Route.useParams()
  const { action } = Route.useSearch()
  const navigate = useNavigate()
  const viewRecorded = useRef<boolean>(false)
  const evalRequested = useRef<boolean>(false)
  const [connectDialogOpen, setConnectDialogOpen] = useState(false)
  const [investorCtaOpen, setInvestorCtaOpen] = useState(false)

  const ctaCountKey = `investor-cta-count-${token}`

  const { data: userResponse } = useCurrentUserQuery()
  const onboardingCompleted = userResponse?.data?.onboardingCompleted ?? false
  const isFounder = useIsFounder()
  const isInvestor = useIsInvestor()

  const { data: dataRoomResponse, isLoading, error } = useDataRoomContentQuery(token)
  const { mutate: recordView } = useRecordDataRoomViewMutation()
  const { data: configResponse } = useStartupConfigQuery()

  const { setSection } = useDataRoomTracker(token)

  const { data: investorResponse } = useInvestorQuery({ enabled: isInvestor })
  const investorId = investorResponse?.data?.id
  const pitchSessionId = dataRoomResponse?.data?.pitchSession?.id
  const requestEvalMutation = useRequestInvestorEvaluationMutation({
    investorId,
    pitchId: pitchSessionId,
    dataRoomToken: token,
  })

  const connectMutation = useConnectWithStartupMutation()
  const { mutateAsync: fetchDataRoomDocumentUrl } = useGetDataRoomDocumentDownloadMutation()
  const { mutateAsync: fetchPitchDeckUrl } = useGetPitchDeckDownloadMutation()
  const { mutateAsync: getVideoUrl } = useGetDataRoomVideoUrlMutation()

  // Auto-request evaluation after onboarding redirect with ?action=request_custom_diligence
  // biome-ignore lint/correctness/useExhaustiveDependencies: mutate identity is unstable, ref guard prevents re-fires
  useEffect(() => {
    if (
      action !== 'request_custom_diligence' ||
      !investorId ||
      !pitchSessionId ||
      evalRequested.current
    )
      return
    evalRequested.current = true
    requestEvalMutation.mutate({ path: { id: investorId, pitchId: pitchSessionId } })
    navigate({ to: '/s/$token', params: { token }, search: {}, replace: true })
  }, [investorId, pitchSessionId, action, navigate])

  const evalAlreadyRequested = requestEvalMutation.isSuccess || requestEvalMutation.isPending

  const handleRequestInvestorEvaluation = () => {
    if (isInvestor && investorId && pitchSessionId) {
      if (evalAlreadyRequested) return
      requestEvalMutation.mutate({ path: { id: investorId, pitchId: pitchSessionId } })
      return
    }
    setAuthRedirect(`/s/${token}?action=request_custom_diligence`)
    navigate({ to: '/join', search: { origin: 'data_room' } })
  }

  const handleVideoClose = () => {
    // Show CTA on 1st, 2nd, and 5th view if user hasn't completed onboarding
    if (!onboardingCompleted) {
      const currentCount = parseInt(localStorage.getItem(ctaCountKey) || '0', 10)
      const newCount = currentCount + 1
      localStorage.setItem(ctaCountKey, String(newCount))

      // Show on 1st, 2nd, and 5th view
      if (newCount === 1 || newCount === 2 || newCount === 5) {
        setInvestorCtaOpen(true)
      }
    }
  }

  const handleCtaClose = (open: boolean) => {
    if (!open) {
      setInvestorCtaOpen(false)
    }
  }

  const dataRoom = dataRoomResponse?.data
  const productStages = configResponse?.data?.productStages ?? []

  // Record view on mount
  useEffect(() => {
    if (!token || !dataRoom) return

    if (!viewRecorded.current) {
      viewRecorded.current = true
      recordView({ path: { token }, body: {} })
    }
  }, [token, dataRoom, recordView])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !dataRoom) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <StatusCard
          variant="error"
          title="Pitch Not Available"
          description="This shared pitch link is invalid or has been revoked."
        />
      </div>
    )
  }

  return (
    <>
      <DataRoomContent
        data={dataRoom}
        productStages={productStages}
        onSectionChange={setSection}
        fetchVideoUrl={
          dataRoom.pitchSession?.videoKey
            ? async () => {
                const response = await getVideoUrl({ path: { token } })
                if (response.data?.url) return response.data.url
                throw new Error('Failed to get video URL')
              }
            : undefined
        }
        fetchPitchDeckUrl={async () => {
          const response = await fetchPitchDeckUrl({ path: { token } })
          if (response.data?.downloadUrl) return response.data.downloadUrl
          throw new Error('Failed to get download URL')
        }}
        fetchDocumentUrl={async (documentId) => {
          const response = await fetchDataRoomDocumentUrl({
            path: { token, documentId },
          })
          if (response.data?.downloadUrl) {
            return response.data.downloadUrl
          }
          throw new Error('Failed to get download URL')
        }}
        onVideoClose={handleVideoClose}
        onRequestInvestorEvaluation={!isFounder ? handleRequestInvestorEvaluation : undefined}
        isRequestingEvaluation={requestEvalMutation.isPending || requestEvalMutation.isSuccess}
        headerActions={
          <Button
            disabled={connectMutation.isPending || connectMutation.isSuccess}
            onClick={() => {
              if (!onboardingCompleted) {
                navigate({ to: '/join' })
              } else {
                setConnectDialogOpen(true)
              }
            }}
          >
            <Handshake className="size-4" />
            {connectMutation.isSuccess ? 'Request Sent' : 'Connect'}
          </Button>
        }
        footer={
          !onboardingCompleted ? (
            <>
              <div className="my-12 md:my-24 h-px bg-linear-to-r from-transparent via-[#f97316]/40 to-transparent" />
              <div className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">Want to explore more?</h3>
                <p className="mt-2 text-muted-foreground">
                  Complete your profile to access the full platform and discover more startups.
                </p>
                <div className="mt-4">
                  <Link to="/join">
                    <Button>Complete Profile</Button>
                  </Link>
                </div>
              </div>
            </>
          ) : null
        }
      />

      <ConnectDialog
        startupName={dataRoom.startup.name}
        open={connectDialogOpen}
        onOpenChange={setConnectDialogOpen}
        onConnect={(message) => {
          connectMutation.mutate(
            {
              path: { token },
              body: { message },
            },
            { onSuccess: () => setConnectDialogOpen(false) }
          )
        }}
        isPending={connectMutation.isPending}
      />

      <InvestorCtaModal open={investorCtaOpen} onOpenChange={handleCtaClose} />
    </>
  )
}
